/**
 * Authentication Service
 * Handles JWT authentication with the backend
 */

import { clearAuthToken, getAuthToken, setAuthToken } from '../lib/api-client';

const AUTH_API_BASE = 'http://localhost:8001';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: UserInfo;
}

export interface UserInfo {
  userId: string;
  username: string;
  role: string;
  permissions?: string[];
}

/**
 * Login with username and password
 */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch(`${AUTH_API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Login failed' }));
    throw new Error(error.error || 'Login failed');
  }

  const data: LoginResponse = await response.json();

  // Store token in localStorage
  setAuthToken(data.token);

  // Store user info
  localStorage.setItem('user_info', JSON.stringify(data.user));

  return data;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  const token = getAuthToken();

  if (token) {
    try {
      // Call WebAuthn logout endpoint via gateway (relative URL uses Vite proxy)
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout request failed:', error);
      // Continue with local logout even if request fails
    }
  }

  // Clear local storage (WebAuthn uses 'user' not 'user_info')
  clearAuthToken();
  localStorage.removeItem('user');
  localStorage.removeItem('user_info');
  localStorage.removeItem('username');
  localStorage.removeItem('user.role');
  localStorage.removeItem('moderation.alias');
}

/**
 * Verify current token and get user info
 */
export async function verifyToken(): Promise<UserInfo | null> {
  const token = getAuthToken();

  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${AUTH_API_BASE}/api/auth/verify`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      // Token is invalid or expired
      clearAuthToken();
      localStorage.removeItem('user_info');
      return null;
    }

    const userInfo: UserInfo = await response.json();

    // Update stored user info
    localStorage.setItem('user_info', JSON.stringify(userInfo));

    return userInfo;
  } catch (error) {
    console.error('Token verification failed:', error);
    clearAuthToken();
    localStorage.removeItem('user_info');
    return null;
  }
}

/**
 * Get stored user info (without verification)
 */
export function getStoredUserInfo(): UserInfo | null {
  // Try 'user' first (WebAuthn uses this), then fall back to 'user_info'
  const stored = localStorage.getItem('user') || localStorage.getItem('user_info');

  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored);
  } catch (error) {
    return null;
  }
}

/**
 * Check if user has a specific role
 */
export function hasRole(role: string): boolean {
  const userInfo = getStoredUserInfo();
  console.log("userInfo",userInfo)
  return userInfo?.role === role;
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(roles: string[]): boolean {
  const userInfo = getStoredUserInfo();
  if (!userInfo) return false;

  return roles.includes(userInfo.role);
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(permission: string): boolean {
  const userInfo = getStoredUserInfo();

  if (!userInfo || !userInfo.permissions) {
    return false;
  }

  return userInfo.permissions.includes(permission);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * Get current user's role
 */
export function getCurrentUserRole(): string | null {
  const userInfo = getStoredUserInfo();
  return userInfo?.role || null;
}

/**
 * Check if user is moderator or admin
 */
export function isModerator(): boolean {
  return hasAnyRole(['moderator', 'admin']);
}

/**
 * Check if user is admin
 */
export function isAdmin(): boolean {
  return hasRole('admin');
}
